import { useLingui } from "@lingui/react/macro";
import { isRouteErrorResponse, useRouteError } from "react-router";
import { Feedback } from "../feedback";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";

export declare class ErrorResponse {
  status: number;
  statusText: string;
  data: any;
  error?: Error;
  internal: boolean;
  constructor(
    status: number,
    statusText: string | undefined,
    data: any,
    internal?: boolean,
  );
}

export function GenericBoundary() {
  const error = useRouteError();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <>
      {isRouteErrorResponse(error) ? (
        <GenericCatchBoundary error={error} />
      ) : (
        <GenericErrorBoundary
          error={error instanceof Error ? error : new Error("Unknown Error")}
        />
      )}
    </>
  );
}

export function GenericErrorBoundary({ error }: { error: Error }) {
  const { t } = useLingui();
  if ((error?.message || "").toLowerCase().includes("not_found")) {
    return (
      <Feedback
        code={404}
        title={t`The AI broke free again. Classic.`}
        subtitle={t`The link may have moved or expired. Double-check the URL or head back home.`}
      />
    );
  }
  if ((error?.message || "").toLowerCase().includes("forbidden")) {
    return (
      <Feedback
        code={403}
        title={t`The AI broke free again. Classic.`}
        subtitle={t`It looks like you don't have permission to access this`}
      />
    );
  }
  return (
    <Feedback
      code={500}
      title={t`The AI broke free again. Classic.`}
      subtitle={t`This request can't be handled at this time. Please try again later.`}
    />
  );
}

export function GenericCatchBoundary({ error }: { error: { status: number } }) {
  const { t } = useLingui();
  if (error.status === 404) {
    return (
      <Feedback
        code={404}
        title={t`The AI broke free again. Classic.`}
        subtitle={t`This page doesn't exist.`}
      />
    );
  }

  if (error.status === 403) {
    return (
      <Feedback
        code={403}
        title={t`The AI broke free again. Classic.`}
        subtitle={t`This resource is not accessible by anyone other than its owner.
          \nPlease ask them for access.`}
      />
    );
  }

  return (
    <Feedback
      code={error.status}
      title={t`The AI broke free again. Classic.`}
      subtitle={t`This request can't be handled at this time. Please try again later.`}
    />
  );
}
